import { Dialog as MuiDialog, dialogClasses, keyframes, styled, type Theme, Typography, useMediaQuery } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { type ReactNode, useEffect, useId, useRef } from 'react';

import { Button } from '../../atoms/Button';

type StepDialogPoint = {
  label: string;
  description: string;
};

type StepDialogStep = {
  id: string;
  title: string;
  description: ReactNode;
  /** Short named items, e.g. the destinations this step introduces. */
  points?: StepDialogPoint[];
};

type StepDialogLabels = {
  skip: string;
  previous: string;
  next: string;
  finish: string;
  /** Announced progress, e.g. (2, 4) => "Étape 2 sur 4". */
  progress: (current: number, total: number) => string;
};

type Props = {
  open: boolean;
  steps: StepDialogStep[];
  activeStep: number;
  onStepChange: (index: number) => void;
  /** Leaves before the last step (skip button or Escape). */
  onSkip: () => void;
  onFinish: () => void;
  labels: StepDialogLabels;
  /** Shared illustration kept mounted across steps, so it can animate between them (e.g. `NavigationPreview`). */
  visual?: ReactNode;
  'data-testid'?: string;
};

const FULL_SCREEN_BREAKPOINT = 'sm';
const PAPER_WIDTH = 720;
const PAPER_MIN_HEIGHT = 480;
const VISUAL_COLUMN_WIDTH = 296;
const DOT_SIZE = 8;
const ENTER_OFFSET = 8;

const stepEnter = keyframes({
  from: { opacity: 0, transform: `translateY(${ENTER_OFFSET}px)` },
  to: { opacity: 1, transform: 'none' },
});

const StyledDialog = styled(MuiDialog)(({ theme }) => ({
  [theme.breakpoints.up(FULL_SCREEN_BREAKPOINT)]: {
    [`& .${dialogClasses.paperWidthFalse}`]: {
      width: PAPER_WIDTH,
      maxWidth: `calc(100% - ${theme.spacing(8)})`,
    },
  },
}));

// Phones: header and footer pinned, the visual and the text scroll together.
// From `sm`: the visual takes a left column; `Scroll` steps aside (`display: contents`) so its children join the grid.
const Layout = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  [theme.breakpoints.up(FULL_SCREEN_BREAKPOINT)]: {
    display: 'grid',
    gridTemplateColumns: `${VISUAL_COLUMN_WIDTH}px minmax(0, 1fr)`,
    gridTemplateRows: 'auto minmax(0, 1fr) auto',
    gridTemplateAreas: '"visual header" "visual body" "visual footer"',
    minHeight: PAPER_MIN_HEIGHT,
  },
}));

const Scroll = styled('div')(({ theme }) => ({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  [theme.breakpoints.up(FULL_SCREEN_BREAKPOINT)]: {
    display: 'contents',
  },
}));

const Header = styled('div')(({ theme }) => ({
  gridArea: 'header',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  minHeight: theme.spacing(7),
  padding: theme.spacing(1, 1, 1, 3),
  paddingTop: `max(${theme.spacing(1)}, env(safe-area-inset-top))`,
}));

const Dots = styled('ol')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  margin: 0,
  padding: 0,
  listStyle: 'none',
}));

const Dot = styled('li')<{ ownerState: { active: boolean } }>(({ theme, ownerState }) => ({
  width: DOT_SIZE,
  height: DOT_SIZE,
  borderRadius: '50%',
  backgroundColor: ownerState.active ? theme.palette.primary.main : theme.palette.action.disabled,
  transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.short }),
}));

const HiddenText = styled('span')(visuallyHidden);

const Visual = styled('div')(({ theme }) => ({
  gridArea: 'visual',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.elevation1,
  borderBlock: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up(FULL_SCREEN_BREAKPOINT)]: {
    padding: theme.spacing(3),
    borderBlock: 'none',
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

const Body = styled('div')(({ theme }) => ({
  gridArea: 'body',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3, 3, 2),
  animation: `${stepEnter} ${theme.transitions.duration.enteringScreen}ms ${theme.transitions.easing.easeOut}`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  [theme.breakpoints.up(FULL_SCREEN_BREAKPOINT)]: {
    paddingTop: theme.spacing(1),
    overflowY: 'auto',
  },
}));

const Title = styled(Typography)({
  textWrap: 'balance',
  '&:focus': {
    outline: 'none',
  },
}) as typeof Typography;

const Description = styled('div')(({ theme }) => ({
  ...theme.typography.body1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  maxWidth: '65ch',
  color: theme.palette.text.primary,
  '& > p': {
    margin: 0,
  },
}));

const Points = styled('dl')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  margin: 0,
}));

const PointLabel = styled('dt')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  color: theme.palette.text.primary,
}));

const PointDescription = styled('dd')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  color: theme.palette.text.secondary,
}));

const Footer = styled('div')(({ theme }) => ({
  gridArea: 'footer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 3),
  paddingBottom: `max(${theme.spacing(2)}, env(safe-area-inset-bottom))`,
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up(FULL_SCREEN_BREAKPOINT)]: {
    borderTop: 'none',
    paddingTop: 0,
  },
}));

// Keeps the primary action on the right when there is no previous step.
const FooterStart = styled('div')({
  display: 'flex',
});

function isSmallScreen(theme: Theme) {
  return theme.breakpoints.down(FULL_SCREEN_BREAKPOINT);
}

/**
 * Short guided sequence in a dialog (first-run onboarding, feature tour): progress, skip, one idea per step.
 * Full screen on phones; from `sm`, the shared `visual` takes a left column beside the text.
 */
export function StepDialog(props: Props) {
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const previousStepRef = useRef(props.activeStep);
  const smallScreen = useMediaQuery(isSmallScreen);

  const total = props.steps.length;
  const step = props.steps[props.activeStep];
  const isFirst = props.activeStep === 0;
  const isLast = props.activeStep === total - 1;

  // A new step replaces the content under the buttons: move focus to its title so it is read out.
  useEffect(() => {
    if (previousStepRef.current !== props.activeStep) {
      titleRef.current?.focus();
    }
    previousStepRef.current = props.activeStep;
  }, [props.activeStep]);

  function handleClose(_event: object, reason: 'backdropClick' | 'escapeKeyDown') {
    // A stray tap next to the dialog must not end the tour; Escape does.
    if (reason === 'escapeKeyDown') {
      props.onSkip();
    }
  }

  function handlePrevious() {
    props.onStepChange(props.activeStep - 1);
  }

  function handleNext() {
    if (isLast) {
      props.onFinish();
      return;
    }
    props.onStepChange(props.activeStep + 1);
  }

  if (!step) {
    return null;
  }

  return (
    <StyledDialog
      open={props.open}
      onClose={handleClose}
      fullScreen={smallScreen}
      maxWidth={false}
      aria-labelledby={titleId}
      data-testid={props['data-testid'] ?? 'step-dialog'}
    >
      <Layout>
        <Header>
          <Dots aria-hidden="true">
            {props.steps.map((item, index) => (
              <Dot key={item.id} ownerState={{ active: index === props.activeStep }} />
            ))}
          </Dots>
          {isLast ? null : (
            <Button variant="text" color="default" size="small" onClick={props.onSkip}>
              {props.labels.skip}
            </Button>
          )}
        </Header>

        <Scroll>
          {props.visual ? <Visual>{props.visual}</Visual> : null}

          <Body key={step.id}>
            <Title id={titleId} ref={titleRef} tabIndex={-1} variant="h3" component="h2">
              <HiddenText>{props.labels.progress(props.activeStep + 1, total)} : </HiddenText>
              {step.title}
            </Title>
            <Description>{step.description}</Description>
            {step.points && step.points.length > 0 ? (
              <Points>
                {step.points.map((point) => (
                  <div key={point.label}>
                    <PointLabel>{point.label}</PointLabel>
                    <PointDescription>{point.description}</PointDescription>
                  </div>
                ))}
              </Points>
            ) : null}
          </Body>
        </Scroll>

        <Footer>
          <FooterStart>
            {isFirst ? null : (
              <Button variant="text" color="default" onClick={handlePrevious}>
                {props.labels.previous}
              </Button>
            )}
          </FooterStart>
          <Button variant="contained" onClick={handleNext}>
            {isLast ? props.labels.finish : props.labels.next}
          </Button>
        </Footer>
      </Layout>
    </StyledDialog>
  );
}

export type {
  Props as StepDialogProps,
  StepDialogLabels,
  StepDialogPoint,
  StepDialogStep,
};
